import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const friendlyError = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.detail) {
    if (Array.isArray(data.detail) && data.detail.length) {
      return data.detail[0]?.msg?.replace(/^Value error,\s*/i, "") || fallback;
    }
    if (typeof data.detail === "string") return data.detail;
  }
  if (err?.response?.status === 429) return "Too many submissions. Please try again in a few minutes.";
  return fallback;
};

export const Join = () => {
  const [email, setEmail] = useState("");
  const [emailHp, setEmailHp] = useState(""); // honeypot
  const [signupBusy, setSignupBusy] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [contactBusy, setContactBusy] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (signupBusy) return;
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSignupBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email, website: emailHp });
      toast.success("Welcome to the movement.", {
        description: "We'll be in touch with what comes next.",
      });
      setEmail("");
    } catch (err) {
      toast.error(friendlyError(err, "Something went wrong. Please try again."));
    } finally {
      setSignupBusy(false);
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (contactBusy) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!form.email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setContactBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        website: form.website,
      });
      toast.success("Thank you for reaching out.", {
        description: "Your note has been received. We read every one.",
      });
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
    } catch (err) {
      toast.error(friendlyError(err, "Something went wrong. Please try again."));
    } finally {
      setContactBusy(false);
    }
  };

  return (
    <section
      id="join"
      data-testid="join-section"
      className="relative bg-[#F9F8F6] py-24 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-16">
          {/* Left — Join the movement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            className="col-span-12 md:col-span-6"
          >
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="font-serif-display text-[100px] md:text-[140px] lg:text-[180px] leading-[0.9] text-[#E8E3DA] font-light block mb-6 md:mb-8"
            >
              06
            </motion.span>
            <p className="text-[13px] md:text-sm uppercase tracking-[0.28em] text-[#C06E52] font-semibold mb-6">
              Join the Movement
            </p>
            <h2
              data-testid="join-heading"
              className="font-serif-display text-[#2D3730] text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] font-light tracking-tight"
            >
              {`You don't have to walk this path `}
              <span className="italic text-[#C06E52]">alone.</span>
            </h2>

            <p className="mt-8 text-[#5C665F] text-base md:text-lg leading-relaxed font-light max-w-[500px]">
              {`Leave your email. We'll send slow, thoughtful letters on emotional fatherhood — never spam, never noise. Just the work, shared with the men walking it with you.`}
            </p>

            <form
              onSubmit={handleSignup}
              data-testid="signup-form"
              noValidate
              className="mt-12 max-w-[520px]"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={emailHp}
                onChange={(e) => setEmailHp(e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />
              <label
                htmlFor="signup-email"
                className="block text-[11px] uppercase tracking-[0.24em] text-[#8A9A86] mb-3"
              >
                Your email
              </label>
              <div className="flex items-end gap-4">
                <input
                  id="signup-email"
                  type="email"
                  data-testid="signup-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="father@yourhome.com"
                  className="editorial-input flex-1"
                  disabled={signupBusy}
                />
                <button
                  type="submit"
                  data-testid="signup-submit"
                  disabled={signupBusy}
                  className="group inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] text-[#2D3730] hover:text-[#C06E52] transition-colors duration-300 pb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {signupBusy ? "Subscribing" : "Subscribe"}
                  {signupBusy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ArrowRight
                      size={14}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  )}
                </button>
              </div>
              <p className="mt-4 text-xs text-[#5C665F]/70 font-light">
                By subscribing you agree to receive occasional letters from FabFathers.
              </p>
            </form>

            <div className="mt-16 md:mt-20 border-t border-[#2D3730]/15 pt-8">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#8A9A86] mb-2">
                Reach us
              </p>
              <a
                href="mailto:Support@fabfathers.com"
                data-testid="email-link"
                className="link-underline font-serif-display text-xl md:text-2xl text-[#2D3730]"
              >
                Support@fabfathers.com
              </a>
            </div>
          </motion.div>

          {/* Right — Contact form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, delay: 0.15 }}
            className="col-span-12 md:col-span-5 md:col-start-8 bg-[#F2EFEB] p-8 md:p-12"
          >
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C06E52] mb-6">
              — Write to us
            </p>
            <h3 className="font-serif-display text-[#2D3730] text-3xl md:text-4xl leading-snug font-light mb-10">
              A story, a question,
              <br />
              a quiet hope.
            </h3>

            <form onSubmit={handleContact} data-testid="contact-form" noValidate className="space-y-8">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />
              <div>
                <label className="block text-[11px] uppercase tracking-[0.24em] text-[#8A9A86] mb-2">
                  Name
                </label>
                <input
                  type="text"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="editorial-input"
                  disabled={contactBusy}
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.24em] text-[#8A9A86] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="your@email.com"
                  className="editorial-input"
                  disabled={contactBusy}
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.24em] text-[#8A9A86] mb-2">
                  Phone <span className="lowercase tracking-normal text-[#5C665F]/60">(optional)</span>
                </label>
                <input
                  type="tel"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 XXXXXXXXXX"
                  className="editorial-input"
                  disabled={contactBusy}
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.24em] text-[#8A9A86] mb-2">
                  Message
                </label>
                <textarea
                  rows="4"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us what brought you here..."
                  className="editorial-input resize-none"
                  disabled={contactBusy}
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={contactBusy}
                className="w-full bg-[#2D3730] text-[#F9F8F6] py-4 text-[12px] uppercase tracking-[0.24em] hover:bg-[#C06E52] transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {contactBusy && <Loader2 size={14} className="animate-spin" />}
                {contactBusy ? "Sending" : "Send Message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
